import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { intersectionRootMarginFromRem } from '../lib/layoutUnits';

const OFFSETS = {
  up: { y: 28 },
  down: { y: -28 },
  left: { x: -32 },
  right: { x: 32 },
  none: {},
};

/**
 * Scroll-triggered entrance. Respects prefers-reduced-motion.
 */
export function ScrollReveal({
  children,
  className,
  delay = 0,
  direction = 'up',
  entrance = 'slide',
  duration = 0.6,
  as = 'div',
  ...rest
}) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;
  const offset = entrance === 'fade' ? {} : OFFSETS[direction] || OFFSETS.up;
  const viewportMargin = useMemo(
    () => intersectionRootMarginFromRem(0, 0, -3.75, 0),
    []
  );
  const motionDuration = entrance === 'fade' ? 0.45 : duration;

  return (
    <Component
      className={className}
      initial={reduceMotion ? false : { opacity: 0, ...offset }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.2, margin: viewportMargin }}
      transition={{
        duration: reduceMotion ? 0 : motionDuration,
        delay: reduceMotion ? 0 : delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

export function StaggerReveal({ children, className, stagger = 0.08, delayChildren = 0.1 }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: reduceMotion ? 0 : stagger,
            delayChildren: reduceMotion ? 0 : delayChildren,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      variants={{
        hidden: reduceMotion ? { opacity: 1 } : { opacity: 0, y: 22 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export default ScrollReveal;
